import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Route, Switch, Redirect, useHistory } from 'react-router-dom';
import { useAsync } from 'react-use';
import { get, pick } from 'lodash';
import { Provider, connect } from 'redux-zero/react';
import { combineActions } from 'redux-zero/utils';
import store from 'app/state/store';
import { formActions, holderActions } from './state/actions';

import {
  AuthenticationService,
  HolderService,
  SessionService,
  TokenService,
  //  MessageService,
  RedemptionService,
  OnchainIdService,
} from 'services';

import AppProviders from './contexts/AppProviders';
import { LoaderDots as Loader } from '@tokenyico/tokeny-ui';

import TemplateAuth from './views/Auth/template/TemplateAuth';
import TemplatePlatform from './views/Platform/template/TemplatePlatform';

import ConfirmSignUpAuthView from './views/Auth/ConfirmSignUpAuthView';
import DashboardView from './views/Platform/views/Dashboard/DashboardView';
import BuySellView from './views/Platform/views/Billboard/BuySellView';
import ForgotPasswordAuthView from './views/Auth/ForgotPasswordAuthView';
import ProfileView from './views/Platform/views/Profile/ProfileView';
import ResetPasswordAuthView from './views/Auth/ResetPasswordAuthView';
import SignInAuthView from './views/Auth/SignInAuthView';
import SignUpAuthView from './views/Auth/SignUpAuthView';
import TransactionsView from './views/Platform/views/Transactions/TransactionsView';
import MessagesView from './views/Platform/views/Messages/MessagesView';
import RequestsView from './views/Platform/views/Requests/RequestsView';
import SubscriptionsOrdersView from './views/Platform/views/Orders/Subscriptions/SubscriptionsOrdersView';
import RedemptionOrdersView from './views/Platform/views/Orders/Redemptions/RedemptionOrdersView';

import {
  DsToast,
  DsConfirm,
  DsTokenSubscription,
  DsIcon,
} from '@tokenyico/design-system/dist/reactJS';

import { useTranslation } from 'react-i18next';

import * as Sentry from '@sentry/browser';
import SSOLoginAuthView from './views/Auth/SSOLoginAuthView';
import VerifyWallet from './components/VerifyWallet';

const Error = ({ error: { message } }) => (
  <div className="">
    <h1>
      <DsIcon name="close" color="white" size={50} />
    </h1>
    <h1>Internal Server Error</h1>
    <h2>Please try again later</h2>
    <h3>{message}</h3>
  </div>
);

const Loading = () => (
  <div className="loading _flex:center">
    <Loader />
  </div>
);

const PublicRoute = connect(({ holder: { user } }) => ({ user }))(({ user, ...rest }) => {
  return user.id ? (
    <Redirect from="/" to="/dashboard" />
  ) : (
    <TemplateAuth>
      <Route {...rest} />
    </TemplateAuth>
  );
});

const PrivateRoute = connect(({ holder: { user } }) => ({ user }))(({ user, ...rest }) => {
  Sentry.configureScope((scope) => {
    scope.setUser({ email: user.email || 'No user authenticated' });
  });

  return user.id ? (
    <TemplatePlatform>
      <Route {...rest} />
    </TemplatePlatform>
  ) : (
    <Redirect from="/" to="/auth/signin" />
  );
});

const mapToProps = ({
  holder: { user, selectedToken },
  ui: {
    form: { error },
  },
}) => ({ error, user, selectedToken });

const App = connect(
  mapToProps,
  combineActions(formActions, holderActions),
)(
  ({
    error,
    user,
    setUser,
    setTokens,
    setSelectedToken,
    setError,
    // setMessages,
    setPendingRequests,
    selectedToken,
    setRedemption,
    resetFormState,
    setDfnsWalletId,
  }) => {
    const [isInitialized, setIsInitialized] = useState(false);
    const portalId = process.env.REACT_APP_PORTAL_ID;
    const { ready } = useTranslation();
    const isLoggedIn = SessionService.isLoggedIn();
    const orderId = new URLSearchParams(window.location.search).get('orderId');
    const [jwt, setJwt] = useState();
    const [unverifiedWallet, setUnverifiedWallet] = useState(false);

    const history = useHistory();

    useAsync(async () => {
      setIsInitialized(false);
      resetFormState();
      try {
        setJwt(await SessionService.getToken());
        if (!isLoggedIn) throw ''; // sign out and redirect to signin form when session is timedout or manual signed out.
        const currentUser = await HolderService.getHolder();
        let tokenId;
        // let tokenAddress;
        if (portalId) {
          const tokens = await TokenService.getTokens();
          setTokens(tokens);
          const selToken = TokenService.getSelectedToken(tokens);
          setSelectedToken(selToken);
          tokenId = selToken.id;
          // tokenAddress = selToken.address;
        } else {
          tokenId = process.env.REACT_APP_TOKEN_ID;
          const balances = await HolderService.getBalances(tokenId);
          const selToken = get(balances, '[0].token', null);
          setSelectedToken(selToken);
          // tokenAddress = get(balances, '[0].token.address', null);
        }

        const currentTokenHolder = await HolderService.getTokenHolder(tokenId);
        setUser({ ...currentUser, ...pick(currentTokenHolder, 'status') });

        const wallets = await OnchainIdService.getWallets();
        const linkedWallet = wallets.find(
          (wallet) =>
            wallet.walletAddress.toLowerCase() === currentTokenHolder.wallet.toLowerCase(),
        );
        if (linkedWallet) {
          setDfnsWalletId(linkedWallet.externalReference);
          if (!linkedWallet.verified) setUnverifiedWallet(linkedWallet);
        }

        // MessageService.getAllMessages(tokenAddress).then((messages) => setMessages(messages));

        TokenService.getTokenRequestsMeReceived(tokenId, {
          limit: 50,
          offset: 0,
          sortBy: null,
          sortDirection: null,
          filter: {
            status: 'PENDING_APPROVAL',
          },
        }).then(({ itemsReturned }) => setPendingRequests(itemsReturned));

        try {
          const redemption = await RedemptionService.getRedemptions(tokenId);
          setRedemption(redemption);
        } catch (e) {
          setRedemption(false);
          // 404 expected
        }
      } catch (e) {
        console.error(e);
        await AuthenticationService.signOut();
        history.push('/auth/signin');
        setError(e);
      } finally {
        setIsInitialized(true);
      }
    }, [selectedToken?.id]);

    useEffect(() => {
      if (!user?.id) setUnverifiedWallet(false);
    }, [user]);

    return (
      <div className="app ">
        <DsToast />
        <DsConfirm />
        {isInitialized && ready && !orderId ? (
          <>
            <div className="banner" />

            {unverifiedWallet && user.id && (
              <VerifyWallet
                unverifiedWallet={unverifiedWallet}
                onVerified={() => setUnverifiedWallet(false)}
              />
            )}

            <Router>
              <Switch>
                <PublicRoute exact path="/auth/ssologin" component={SSOLoginAuthView} />
                <PublicRoute exact path="/auth/signin" component={SignInAuthView} />
                <PublicRoute exact path="/auth/signup" component={SignUpAuthView} />
                <PublicRoute
                  exact
                  path={['/confirm', '/auth/confirm-signup']}
                  component={ConfirmSignUpAuthView}
                />

                <PublicRoute exact path="/auth/reset-password" component={ResetPasswordAuthView} />
                <PublicRoute
                  exact
                  path="/auth/forgot-password"
                  component={ForgotPasswordAuthView}
                />

                <PrivateRoute exact path="/dashboard" component={DashboardView} />
                <PrivateRoute exact path="/billboard" component={BuySellView} />
                <PrivateRoute path="/profile" component={ProfileView} />
                <PrivateRoute exact path="/transactions" component={TransactionsView} />
                <PrivateRoute exact path="/messages" component={MessagesView} />
                <PrivateRoute exact path="/requests" component={RequestsView} />
                <PrivateRoute exact path="/orders">
                  <Redirect to="/orders/subscriptions" />
                </PrivateRoute>
                <PrivateRoute
                  exact
                  path="/orders/subscriptions"
                  component={SubscriptionsOrdersView}
                />
                <PrivateRoute exact path="/orders/redemptions" component={RedemptionOrdersView} />
                {user.id ? (
                  <Redirect from="/" to="/dashboard" />
                ) : (
                  <Redirect from="/" to="/auth/signin" />
                )}
              </Switch>
            </Router>
          </>
        ) : orderId ? (
          selectedToken && jwt ? (
            <DsTokenSubscription
              servicingApi={process.env.REACT_APP_API_URL_SERVICING}
              tokenId={selectedToken.id}
              jwt={jwt}
            />
          ) : (
            ''
          )
        ) : (
          <>{error && isInitialized ? <Error error={error} /> : <Loading />}</>
        )}
      </div>
    );
  },
);

const AppWithProviders = () => (
  <Provider store={store}>
    <AppProviders>
      <App />
    </AppProviders>
  </Provider>
);

export default AppWithProviders;
